import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Images, Medal, Newspaper, Search, Trophy } from "lucide-react";
import { motion } from "framer-motion";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SectionHeading } from "@/components/SectionHeading";
import { ChronicleGallery } from "@/components/ChronicleGallery";
import { ShareButtons } from "@/components/ShareButtons";
import { formatDate, slugify } from "@/utils/format";

export function WinnersChronicles({ chronicles = [] }) {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [modality, setModality] = useState("Todas");
  const [category, setCategory] = useState("Todas");
  const modalities = useMemo(() => ["Todas", ...new Set(chronicles.map((item) => item.modality).filter(Boolean))], [chronicles]);
  const categories = useMemo(() => ["Todas", ...new Set(chronicles.map((item) => item.category || "Crónica"))], [chronicles]);
  const filtered = [...chronicles]
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
    .filter((item) => {
      const text = `${item.title} ${item.winner} ${item.location} ${item.classifications?.map((row) => row.name).join(" ")}`.toLowerCase();
      return text.includes(search.toLowerCase()) && (modality === "Todas" || item.modality === modality) && (category === "Todas" || (item.category || "Crónica") === category);
    });
  const featuredIndex = Math.max(0, filtered.findIndex((item) => (item.category || "Crónica") === "Crónica"));
  const featured = filtered[featuredIndex];
  const rest = filtered.filter((_, index) => index !== featuredIndex);
  const isResults = (item) => (item?.category || "Crónica") === "Crónica";

  return (
    <section id="cronicas" className="section-shell bg-panel/45" data-testid="chronicles-section">
      <span id="resultados" className="block -translate-y-32" aria-hidden="true" />
      <SectionHeading number="02" eyebrow="Crónicas y premiados" title="Crónicas, carteles, sorteos y noticias" description="Crónicas de tiradas ya celebradas con su podio y clasificación, además de carteles, sorteos de escuadras y noticias generales." />

      <div className="mb-10 grid gap-4 lg:grid-cols-[380px_1fr]" data-testid="chronicles-controls">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-clay" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar tirada, tirador o localidad" className="h-12 rounded-none border-white/10 bg-charcoal pl-11 text-slate-100 placeholder:text-slate-500" data-testid="chronicle-search-input" />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          {categories.length > 2 ? <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Tipo</span> : null}
          {categories.length > 2 ? categories.map((item) => (
            <button key={`cat-${item}`} type="button" onClick={() => setCategory(item)} className={`filter-chip ${category === item ? "filter-chip-active" : ""}`} data-testid={`chronicle-category-${slugify(item)}`}>
              {item}
            </button>
          )) : null}
          {categories.length > 2 ? <span className="hidden h-6 w-px bg-white/15 sm:block" aria-hidden="true" /> : null}
          {categories.length > 2 ? <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Modalidad</span> : null}
          {modalities.map((item) => (
            <button key={item} type="button" onClick={() => setModality(item)} className={`filter-chip ${modality === item ? "filter-chip-active" : ""}`} data-testid={`chronicle-filter-${slugify(item)}`}>
              {item}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-white/15 p-8 text-slate-400" data-testid="chronicles-empty-state">Ninguna crónica coincide con la búsqueda.</div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]" data-testid="chronicles-grid">
        {featured ? (
          <motion.article initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="poster-card group overflow-hidden" data-testid={`chronicle-featured-${featured.id}`}>
            <div className="relative h-[430px] overflow-hidden">
              <ChroniclePhoto chronicle={featured} className="h-full w-full object-cover image-treatment transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/25 to-transparent" />
              {featured.score != null ? (
                <div className="absolute right-6 top-6 border border-clay/50 bg-charcoal/80 px-4 py-3 text-center backdrop-blur">
                  <span className="block font-display text-5xl leading-none text-clay" data-testid={`chronicle-score-${featured.id}`}>{featured.score}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">platos</span>
                </div>
              ) : null}
              <div className="absolute bottom-0 left-0 right-0 p-7 pb-12 sm:p-10 sm:pb-12">
                <h3 className="max-w-3xl font-display text-5xl uppercase leading-[0.88] text-slate-50 sm:text-7xl">{featured.title}</h3>
              </div>
              <div className="absolute bottom-0 left-0 right-0 flex flex-wrap gap-x-4 gap-y-1 bg-charcoal/55 px-7 py-2 backdrop-blur-sm sm:px-10" data-testid={`chronicle-tags-${featured.id}`}>
                {[...new Set([featured.category || "Crónica", featured.modality, ...featured.tags].filter(Boolean))].map((tag) => <span key={tag} className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">{tag}</span>)}
              </div>
            </div>
            <div className="p-7 sm:p-10">
              <WinnerStrip chronicle={featured} />
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-300">{featured.excerpt}</p>
              <ChronicleGallery chronicle={featured} />
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <Button type="button" onClick={() => setSelected(featured)} className="rounded-none bg-clay font-mono text-xs uppercase tracking-[0.22em] text-charcoal hover:bg-brass" data-testid={`chronicle-open-${featured.id}`}>
                  {isResults(featured) ? "Crónica y clasificación" : "Leer noticia"} <Newspaper className="h-4 w-4" />
                </Button>
                <FlickrLink chronicle={featured} />
              </div>
              <div className="mt-5"><ShareButtons chronicle={featured} compact /></div>
            </div>
          </motion.article>
        ) : null}

        <div className="grid gap-6">
          {rest.map((chronicle, index) => (
            <motion.article key={chronicle.id} initial={{ opacity: 0, x: 36 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="poster-card grid overflow-hidden sm:grid-cols-[180px_1fr]" data-testid={`chronicle-card-${chronicle.id}`}>
              <div className="relative h-48 sm:h-full">
                <ChroniclePhoto chronicle={chronicle} className="h-full w-full object-cover image-treatment" />
                {chronicle.score != null ? <span className="absolute bottom-3 left-3 border border-clay/50 bg-charcoal/85 px-2 py-1 font-display text-2xl leading-none text-clay" data-testid={`chronicle-score-${chronicle.id}`}>{chronicle.score}</span> : null}
              </div>
              <div className="p-6">
                <p className="eyebrow">{formatDate(chronicle.date)} · {chronicle.location}{chronicle.modality ? ` · ${chronicle.modality}` : ""}</p>
                <h3 className="mt-3 font-display text-3xl uppercase leading-[0.9] text-slate-50">{chronicle.title}</h3>
                <p className="mt-2 flex items-center gap-2 text-sm text-brass" data-testid={`chronicle-winner-${chronicle.id}`}>{isResults(chronicle) ? <Trophy className="h-4 w-4" /> : <Newspaper className="h-4 w-4" />}{isResults(chronicle) ? chronicle.winner : chronicle.category}</p>
                <p className="mt-3 line-clamp-2 text-sm leading-7 text-slate-400">{chronicle.excerpt}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button type="button" variant="outline" onClick={() => setSelected(chronicle)} className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.2em] hover:border-clay/60 hover:bg-clay/10 hover:text-clay" data-testid={`chronicle-open-${chronicle.id}`}>
                    {isResults(chronicle) ? "Crónica y clasificación" : "Leer noticia"}
                  </Button>
                  <FlickrLink chronicle={chronicle} compact />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-none border-white/10 bg-panel text-slate-100" data-testid="chronicle-detail-dialog">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-5xl uppercase leading-none">{selected.title}</DialogTitle>
                <DialogDescription className="text-slate-400">{selected.category || "Crónica"} · {formatDate(selected.date)} · {selected.location} · {selected.modality}{selected.winner ? ` · Ganador/a: ${selected.winner}` : ""}{selected.author ? ` · Por ${selected.author}` : ""}{selected.score ? ` · ${selected.score} platos` : ""}</DialogDescription>
              </DialogHeader>
              <ChroniclePhoto chronicle={selected} className="max-h-[48vh] w-full object-cover image-treatment" hideWhenMissing />
              <p className="text-lg leading-8 text-slate-200">{selected.excerpt}</p>
              <ChronicleGallery chronicle={selected} />
              <p className="whitespace-pre-line text-base leading-8 text-slate-300">{selected.content}</p>
              <ClassificationTable chronicle={selected} />
              <div className="border border-clay/25 bg-clay/10 p-5">
                <p className="eyebrow">Resumen para prensa y redes</p>
                <p className="mt-3 leading-7 text-slate-200">{selected.social_summary}</p>
              </div>
              <FlickrLink chronicle={selected} />
              <div className="flex flex-wrap items-center gap-3 border-t border-white/10 pt-5">
                <ShareButtons chronicle={selected} compact />
                {selected.slug ? <Link to={`/cronica/${selected.slug}`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay hover:text-brass" data-testid={`chronicle-page-link-${selected.id}`}>Abrir página propia →</Link> : null}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export function ChroniclePhoto({ chronicle, className, hideWhenMissing = false }) {
  const [failed, setFailed] = useState(false);
  if (!chronicle.image || failed) {
    if (hideWhenMissing) return null;
    return <div className="grid h-full min-h-48 w-full place-items-center bg-panel font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500" data-testid={`chronicle-no-image-${chronicle.id}`}>Sin fotografía</div>;
  }
  return <img src={chronicle.image} alt={`Premiados: ${chronicle.title}`} onError={() => setFailed(true)} className={className} data-testid={`chronicle-photo-${chronicle.id}`} />;
}

export function WinnerStrip({ chronicle }) {
  const podium = (chronicle.classifications || []).slice(0, 3);
  if (!podium.length && !chronicle.winner) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid={`chronicle-podium-${chronicle.id}`}>
      {(podium.length ? podium : [{ position: 1, name: chronicle.winner, category: "Absoluta", score: chronicle.score }]).map((row) => (
        <div key={row.position} className="flex items-center gap-3 border border-white/10 bg-white/[0.03] p-3">
          <Badge className="rounded-none border-clay/40 bg-clay/10 text-clay"><Medal className="mr-1 h-3 w-3" />{row.position}</Badge>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-50">{row.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{row.category}{row.score != null ? ` · ${row.score}` : ""}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ClassificationTable({ chronicle }) {
  if ((chronicle.category || "Crónica") !== "Crónica" && !chronicle.classifications?.length) return null;
  if (!chronicle.classifications?.length) {
    return <p className="border border-dashed border-white/15 p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="classification-pending">Clasificación completa pendiente</p>;
  }
  return (
    <Table data-testid="classification-table">
      <TableHeader>
        <TableRow className="border-white/10 hover:bg-transparent">
          <TableHead className="text-slate-400">Pos.</TableHead>
          <TableHead className="text-slate-400">Tirador/a</TableHead>
          <TableHead className="text-slate-400">Categoría</TableHead>
          <TableHead className="text-slate-400">Series</TableHead>
          <TableHead className="text-right text-slate-400">Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {chronicle.classifications.map((row) => (
          <TableRow key={`${chronicle.id}-${row.position}`} className="border-white/10 hover:bg-clay/5" data-testid={`classification-row-${row.position}`}>
            <TableCell><Badge className="rounded-none border-clay/40 bg-clay/10 text-clay"><Medal className="mr-1 h-3 w-3" />{row.position}</Badge></TableCell>
            <TableCell className="font-semibold text-slate-100">{row.name}</TableCell>
            <TableCell className="text-slate-400">{row.category}</TableCell>
            <TableCell className="font-mono text-slate-300">{(row.series || []).join(" · ")}</TableCell>
            <TableCell className="text-right font-display text-3xl text-clay">{row.score ?? "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function FlickrLink({ chronicle, compact = false }) {
  const albums = chronicle.flickr_albums?.length
    ? chronicle.flickr_albums
    : chronicle.flickr_url
      ? [{ label: "Álbum principal", url: chronicle.flickr_url }]
      : [];

  if (albums.length) {
    return (
      <div className="flex flex-wrap gap-2" data-testid={`flickr-albums-${chronicle.id}`}>
        {albums.map((album, index) => (
          <Button key={`${album.url}-${index}`} asChild variant="outline" className={`h-auto rounded-none border-clay/40 bg-clay/10 py-1 pl-1 font-mono ${compact ? "text-[10px]" : "text-xs"} uppercase tracking-[0.2em] text-clay hover:bg-clay hover:text-charcoal`}>
            <a href={album.url} target="_blank" rel="noreferrer" data-testid={`flickr-link-${chronicle.id}-${index}`}>
              {album.cover_url ? <img src={album.cover_url} alt="" className="h-8 w-11 object-cover image-treatment" /> : null}
              {album.label || album.location || "Álbum Flickr"} <Images className="h-4 w-4" /><ArrowUpRight className="h-3 w-3" />
            </a>
          </Button>
        ))}
      </div>
    );
  }

  if ((chronicle.category || "Crónica") !== "Crónica") return null;
  return (
    <span className="border border-dashed border-white/15 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid={`flickr-pending-${chronicle.id}`}>
      Álbum Flickr pendiente
    </span>
  );
}
